import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Drawer, Button, Divider } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import LogoutIcon from '@mui/icons-material/Logout';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import NavigationItems from '../NavigationItems/NavigationItems';
import { AIDE_URL } from '../../../config/config';
import { logout } from '../../../slices/authSlice';

const useStyles = makeStyles()((theme) => ({
  drawer: {
    width: 240,
    [theme.breakpoints.up('lg')]: {
      marginTop: 64,
      height: 'calc(100% - 64px)',
    },
  },
  root: {
    backgroundColor: theme.palette.white,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  nav: {
    marginBottom: theme.spacing(2),
  },
  helpButton: {
    // Aligné sur les entrées du menu qui le suivent : le libellé à gauche,
    // l'icône à droite, la même hauteur de ligne. Une aide qui ressemble à un
    // bouton d'action se lit comme une action.
    justifyContent: 'space-between',
    padding: '10px 8px',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
  },
  signOutButton: {
    // 🔑 « marginTop: auto » le pousse tout en bas du panneau, quelle que
    // soit la longueur du menu. C'est là qu'on va le chercher — c'est aussi
    // la place qu'il a sur Feuille de temps, et les deux produits se
    // ressemblent assez pour qu'une habitude prise sur l'un serve sur l'autre.
    marginTop: 'auto',
    padding: '10px 8px',
    // Le libellé à gauche, l'icône à droite : la ligne se lit comme les
    // entrées du menu au-dessus, alors qu'un bouton centré ferait un objet
    // d'une autre nature.
    justifyContent: 'space-between',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightMedium,
    // Se déconnecter interrompt ce qu'on faisait : la couleur le distingue
    // des déplacements ordinaires, sans en faire une alerte.
    color: theme.palette.error.main,
  },
}));

const SideBar = ({
  open, variant, onClose,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation('common');

  const dispatch = useDispatch();
  const handleLogout = (event) => {
    event.preventDefault();
    dispatch(logout());
  };

  return (

    <Drawer
      anchor="right"
      classes={{ paper: classes.drawer }}
      onClose={onClose}
      open={open}
      variant={variant}
    >
      <div
        className={clsx(classes.root)}
      >
        {/* 🔑 En TÊTE du menu, comme sur Feuille de temps. Quelqu'un qui
            cherche de l'aide ouvre le menu et regarde en haut — pas au milieu
            d'une liste d'écrans, où l'aide se lit comme une destination de
            plus. */}
        <Button
          className={classes.helpButton}
          href={AIDE_URL}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<HelpOutlineIcon />}
        >
          {t('navbar.help')}
        </Button>
        <Divider className={classes.divider} />

        <NavigationItems />
        {/* ⛔ Le bouton n'est plus dans le bandeau. Il y occupait une place
            permanente à côté du menu, sans libellé, et une icône seule ne dit
            pas qu'elle déconnecte. Il était par ailleurs enveloppé dans
            « Hidden », supprimé par MUI v5 : sur les très grands écrans il
            disparaissait purement et simplement. */}
        <Button
          className={classes.signOutButton}
          onClick={handleLogout}
          endIcon={<LogoutIcon />}
        >
          {t('navbar.signOut')}
        </Button>
      </div>
    </Drawer>

  );
};

SideBar.propTypes = {
  open: PropTypes.bool.isRequired,
  variant: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SideBar;
